/**
 * Image utility functions for resizing and processing uploaded images
 */

export interface ResizeOptions {
  width: number;
  height: number;
  quality?: number;
  format?: 'jpeg' | 'png' | 'webp';
}

export interface LogoSpacing {
  isSquare: boolean;
  aspectRatio: number;
  containerClass: string;
  imageClass: string;
}

/**
 * Resize an image file to specified dimensions
 * @param file - The image file to resize
 * @param options - Resize options including width, height, quality, and format
 * @returns Promise<string> - Base64 data URL of the resized image
 */
export const resizeImage = (file: File, options: ResizeOptions): Promise<string> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    if (!ctx) {
      reject(new Error('Canvas context not available'));
      return;
    }

    img.onload = () => {
      // Set canvas dimensions to target size
      canvas.width = options.width;
      canvas.height = options.height;

      // Calculate scaling to maintain aspect ratio while filling the space
      const imgAspectRatio = img.width / img.height;
      const targetAspectRatio = options.width / options.height;

      let drawWidth, drawHeight, offsetX = 0, offsetY = 0;

      if (imgAspectRatio > targetAspectRatio) {
        // Image is wider than target - fit by height
        drawHeight = options.height;
        drawWidth = drawHeight * imgAspectRatio;
        offsetX = (options.width - drawWidth) / 2;
      } else {
        // Image is taller than target - fit by width
        drawWidth = options.width;
        drawHeight = drawWidth / imgAspectRatio;
        offsetY = (options.height - drawHeight) / 2;
      }

      // Fill background with white (in case image doesn't cover entire area)
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, options.width, options.height);

      // Draw the image centered and scaled
      ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

      // Convert to data URL with specified quality and format
      const quality = options.quality || 0.9;
      const format = options.format || 'jpeg';
      const mimeType = `image/${format}`;
      
      const dataUrl = canvas.toDataURL(mimeType, quality);
      resolve(dataUrl);
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    // Create object URL for the file and load it
    const objectUrl = URL.createObjectURL(file);
    img.src = objectUrl;

    // Clean up object URL after image loads
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      
      // Set canvas dimensions to target size
      canvas.width = options.width;
      canvas.height = options.height;

      // Calculate scaling to maintain aspect ratio while filling the space
      const imgAspectRatio = img.width / img.height;
      const targetAspectRatio = options.width / options.height;

      let drawWidth, drawHeight, offsetX = 0, offsetY = 0;

      if (imgAspectRatio > targetAspectRatio) {
        // Image is wider than target - fit by height
        drawHeight = options.height;
        drawWidth = drawHeight * imgAspectRatio;
        offsetX = (options.width - drawWidth) / 2;
      } else {
        // Image is taller than target - fit by width
        drawWidth = options.width;
        drawHeight = drawWidth / imgAspectRatio;
        offsetY = (options.height - drawHeight) / 2;
      }

      // Fill background with white (in case image doesn't cover entire area)
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, options.width, options.height);

      // Draw the image centered and scaled
      ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

      // Convert to data URL with specified quality and format
      const quality = options.quality || 0.9;
      const format = options.format || 'jpeg';
      const mimeType = `image/${format}`;
      
      const dataUrl = canvas.toDataURL(mimeType, quality);
      resolve(dataUrl);
    };
  });
};

/**
 * Analyze image dimensions and determine optimal logo spacing
 * @param dimensions - Image width and height
 * @returns LogoSpacing configuration
 */
export const analyzeLogoSpacing = (dimensions: { width: number; height: number }): LogoSpacing => {
  const { width, height } = dimensions;
  const aspectRatio = width / height;
  
  // Consider square if aspect ratio is between 0.9 and 1.1 (10% tolerance)
  const isSquare = aspectRatio >= 0.9 && aspectRatio <= 1.1;
  
  if (isSquare) {
    return {
      isSquare: true,
      aspectRatio,
      containerClass: 'w-20 h-20', // Square container
      imageClass: 'w-20 h-20 object-cover rounded-xl'
    };
  } else if (aspectRatio > 1.1) {
    // Wide/rectangular logo
    return {
      isSquare: false,
      aspectRatio,
      containerClass: 'w-28 h-20', // Wider container for rectangular logos
      imageClass: 'w-28 h-20 object-cover rounded-xl'
    };
  } else {
    // Tall logo
    return {
      isSquare: false,
      aspectRatio,
      containerClass: 'w-16 h-24', // Taller container for tall logos
      imageClass: 'w-16 h-24 object-cover rounded-xl'
    };
  }
};

/**
 * Resize image for banner with adaptive dimensions based on aspect ratio
 * @param file - The image file to resize
 * @param originalDimensions - Original image dimensions
 * @returns Promise<{ resizedImageUrl: string, spacing: LogoSpacing }>
 */
export const resizeLogoForBannerAdaptive = async (
  file: File, 
  originalDimensions: { width: number; height: number }
): Promise<{ resizedImageUrl: string; spacing: LogoSpacing }> => {
  const spacing = analyzeLogoSpacing(originalDimensions);
  
  // Determine target dimensions based on logo type
  let targetWidth, targetHeight;
  
  if (spacing.isSquare) {
    targetWidth = targetHeight = 80; // Square: 80x80
  } else if (spacing.aspectRatio > 1.1) {
    // Wide logo: maintain aspect ratio within 112x80 bounds
    targetWidth = 112;
    targetHeight = 80;
  } else {
    // Tall logo: maintain aspect ratio within 64x96 bounds
    targetWidth = 64;
    targetHeight = 96;
  }
  
  const resizedImageUrl = await resizeImage(file, {
    width: targetWidth,
    height: targetHeight,
    quality: 0.9,
    format: 'jpeg'
  });
  
  return {
    resizedImageUrl,
    spacing
  };
};

/**
 * Resize image specifically for advertisement banners
 * Standard size: 80x80 pixels for optimal banner display
 */
export const resizeLogoForBanner = (file: File): Promise<string> => {
  return resizeImage(file, {
    width: 80,
    height: 80,
    quality: 0.9,
    format: 'jpeg'
  });
};

/**
 * Validate image file before processing
 * @param file - The file to validate
 * @returns boolean - True if file is a valid image
 */
export const validateImageFile = (file: File): boolean => {
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  const maxSize = 5 * 1024 * 1024; // 5MB

  if (!validTypes.includes(file.type)) {
    return false;
  }

  if (file.size > maxSize) {
    return false;
  }

  return true;
};

/**
 * Get image dimensions from file
 * @param file - The image file
 * @returns Promise with width and height
 */
export const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      resolve({
        width: img.width,
        height: img.height
      });
      URL.revokeObjectURL(img.src);
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
      URL.revokeObjectURL(img.src);
    };

    img.src = URL.createObjectURL(file);
  });
};

/**
 * Get logo spacing configuration from image URL
 * @param imageUrl - The image URL to analyze
 * @returns Promise<LogoSpacing> - Spacing configuration
 */
export const getLogoSpacingFromUrl = (imageUrl: string): Promise<LogoSpacing> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      const spacing = analyzeLogoSpacing({
        width: img.width,
        height: img.height
      });
      resolve(spacing);
    };

    img.onerror = () => {
      // Fallback to square spacing if image fails to load
      resolve({
        isSquare: true,
        aspectRatio: 1,
        containerClass: 'w-20 h-20',
        imageClass: 'w-20 h-20 object-cover rounded-xl'
      });
    };

    img.src = imageUrl;
  });
};